/**
 * Staff sign-in code — when the login page signs in by itself
 * (login-code-autofill, 2026-10-10).
 *
 * WHY. On an iPhone the code arrives in a WhatsApp notification and iOS 26
 * offers it above the keyboard; one tap puts the whole code in the box. A
 * person who has done that has nothing left to give, so making them press
 * "Sign in with code" as well is a step with no information in it. The same
 * goes for a paste.
 *
 * THE RULE. The page signs in when a COMPLETE code lands in ONE edit: the
 * box's new value is a code the server would accept, and that single edit
 * put in at least a whole code's worth of digits. Typing puts in one digit
 * per edit and so never qualifies — a typed code keeps its button, because
 * the page cannot know a typed code is finished.
 *
 * WHY NOT "when the box reaches N digits". The length is Supabase's
 * `mailer_otp_length`, a project setting this page cannot read, which is why
 * the server accepts six to ten digits. A rule keyed on a count would submit
 * a longer code early, or never submit a shorter one, the day that setting
 * changes.
 */

/** The server's accept rule for a submitted code (`staff-login-link.ts`, `verify`). */
const SIGN_IN_CODE = /^\d{6,10}$/;

/** The shortest code the server accepts: an edit that puts in this many digits is not typing. */
const WHOLE_CODE_DIGITS = 6;

/**
 * The digits one edit put in: what is left of `next` once the start and the
 * end it shares with `prev` are set aside. It counts what the edit put in and
 * not how much longer the value got, because a new code pasted over a refused
 * one leaves the box no fuller than it was.
 */
function digitsPutIn(prev: string, next: string): number {
  const shorter = Math.min(prev.length, next.length);
  let start = 0;
  while (start < shorter && prev[start] === next[start]) start += 1;
  let end = 0;
  while (
    end < shorter - start &&
    prev[prev.length - 1 - end] === next[next.length - 1 - end]
  ) {
    end += 1;
  }
  return next.slice(start, next.length - end).replace(/\D/g, "").length;
}

/**
 * True when the edit from `prev` to `next` delivered a complete sign-in code
 * in one go — a keyboard suggestion or a paste — rather than a keystroke.
 * Surrounding whitespace is ignored, as the submit trims it.
 */
export function arrivedWhole(prev: string, next: string): boolean {
  if (!SIGN_IN_CODE.test(next.trim())) return false;
  return digitsPutIn(prev, next) >= WHOLE_CODE_DIGITS;
}
